import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSitemap, faPlus, faPen, faTrash, faArrowUp, faArrowDown } from '@fortawesome/free-solid-svg-icons';
import api from '../utils/api';
import { useOrgOptions, refreshOrgOptions } from '../utils/orgOptions';

// Admin Settings card for the department and designation lists the employee
// forms offer. Renaming moves every employee with the old name to the new
// one; an entry still in use cannot be deleted (server/routes/orgOptions.js).

const iconBtn = { padding: '5px 9px', fontSize: '12px' };

const OrgList = ({ kind, title, singular, rows, loading }) => {
    const [name, setName] = useState('');
    const [busy, setBusy] = useState(false);

    const run = async (action) => {
        setBusy(true);
        try {
            await action();
            await refreshOrgOptions();
            return true;
        } catch (err) {
            Swal.fire('Error', err.response?.data?.message || 'Something went wrong.', 'error');
            return false;
        } finally {
            setBusy(false);
        }
    };

    const add = async (e) => {
        e.preventDefault();
        if (!name.trim()) return;
        if (await run(() => api.post('/org-options', { kind, name }))) setName('');
    };

    const rename = async (row) => {
        const { value } = await Swal.fire({
            title: `Rename ${singular}`,
            input: 'text',
            inputValue: row.name,
            text: row.count ? `${row.count} employee${row.count === 1 ? '' : 's'} will move to the new name.` : undefined,
            showCancelButton: true,
            confirmButtonText: 'Save',
            confirmButtonColor: '#215D7B',
            inputValidator: (v) => (!v.trim() ? 'Enter a name' : undefined)
        });
        if (!value || value.trim() === row.name) return;
        const res = await api.put(`/org-options/${row._id}`, { name: value }).catch(err => {
            Swal.fire('Error', err.response?.data?.message || 'Could not rename.', 'error');
            return null;
        });
        if (!res) return;
        await refreshOrgOptions();
        const moved = res.data.employeesUpdated;
        if (moved) Swal.fire({ icon: 'success', title: 'Renamed', text: `${moved} employee${moved === 1 ? '' : 's'} updated.`, confirmButtonColor: '#215D7B' });
    };

    const remove = async (row) => {
        if (row.count > 0) {
            Swal.fire({
                icon: 'info',
                title: 'Still in use',
                text: `${row.count} employee${row.count === 1 ? '' : 's'} ${row.count === 1 ? 'has' : 'have'} "${row.name}". Move them to another ${singular} first.`,
                confirmButtonColor: '#215D7B'
            });
            return;
        }
        const res = await Swal.fire({
            title: `Delete "${row.name}"?`,
            text: `It will no longer be offered on the employee forms.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Delete'
        });
        if (res.isConfirmed) run(() => api.delete(`/org-options/${row._id}`));
    };

    const move = (index, delta) => {
        const next = [...rows];
        const [item] = next.splice(index, 1);
        next.splice(index + delta, 0, item);
        run(() => api.put('/org-options/reorder', { kind, ids: next.map(r => r._id) }));
    };

    return (
        <div style={{ minWidth: 0 }}>
            <h4 style={{ margin: '0 0 10px', fontSize: '14px', color: 'light-dark(#334155, var(--theme-ink-soft, #cfd3d9))' }}>
                {title} <span style={{ fontWeight: 400, color: 'light-dark(#94a3b8, var(--theme-subtle, #7a808a))' }}>({rows.length})</span>
            </h4>

            <form onSubmit={add} style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                <input
                    className="custom-input"
                    style={{ flex: 1, minWidth: 0 }}
                    value={name}
                    maxLength={60}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={`New ${singular}`}
                    aria-label={`New ${singular}`}
                />
                <button type="submit" className="gts-btn primary" disabled={busy || !name.trim()} style={{ padding: '8px 14px', fontSize: '13px' }}>
                    <FontAwesomeIcon icon={faPlus} /> Add
                </button>
            </form>

            <div style={{ border: '1px solid light-dark(#e2e8f0, var(--theme-border, #33363b))', borderRadius: '8px' }}>
                {loading ? (
                    <div style={{ padding: '14px', fontSize: '13px', color: 'light-dark(#94a3b8, var(--theme-subtle, #7a808a))' }}>Loading…</div>
                ) : rows.length === 0 ? (
                    <div style={{ padding: '14px', fontSize: '13px', color: 'light-dark(#94a3b8, var(--theme-subtle, #7a808a))' }}>Nothing added yet.</div>
                ) : rows.map((row, i) => (
                    <div
                        key={row._id}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px',
                            borderBottom: i < rows.length - 1 ? '1px solid light-dark(#f1f5f9, var(--theme-border, #33363b))' : 'none'
                        }}
                    >
                        <span style={{ flex: 1, minWidth: 0, fontSize: '14px', color: 'light-dark(#333, var(--theme-ink, #dcdfe4))', overflowWrap: 'anywhere' }}>{row.name}</span>
                        <span
                            title={`${row.count} employee${row.count === 1 ? '' : 's'}`}
                            style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '999px', background: 'light-dark(#f1f5f9, var(--theme-surface-soft, #202327))', color: 'light-dark(#64748b, var(--theme-muted, #959ba5))', whiteSpace: 'nowrap' }}
                        >
                            {row.count}
                        </span>
                        <button type="button" className="gts-btn" style={iconBtn} disabled={busy || i === 0} onClick={() => move(i, -1)} title="Move up" aria-label={`Move ${row.name} up`}>
                            <FontAwesomeIcon icon={faArrowUp} />
                        </button>
                        <button type="button" className="gts-btn" style={iconBtn} disabled={busy || i === rows.length - 1} onClick={() => move(i, 1)} title="Move down" aria-label={`Move ${row.name} down`}>
                            <FontAwesomeIcon icon={faArrowDown} />
                        </button>
                        <button type="button" className="gts-btn" style={iconBtn} disabled={busy} onClick={() => rename(row)} title="Rename" aria-label={`Rename ${row.name}`}>
                            <FontAwesomeIcon icon={faPen} />
                        </button>
                        <button type="button" className="gts-btn danger" style={iconBtn} disabled={busy} onClick={() => remove(row)} title="Delete" aria-label={`Delete ${row.name}`}>
                            <FontAwesomeIcon icon={faTrash} />
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
};

const OrgOptionsManager = () => {
    const { entries, loading } = useOrgOptions();

    // Employee counts move whenever someone is edited, so fetch fresh ones here.
    useEffect(() => { refreshOrgOptions(); }, []);

    return (
        <div className="control-card" style={{ display: 'flex', flexWrap: 'wrap', gap: '40px', alignItems: 'flex-start', marginBottom: '20px' }}>
            <div style={{ flex: '0 0 280px' }}>
                <h3 style={{ margin: 0, color: 'light-dark(#215D7B, var(--theme-accent-ink))', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <FontAwesomeIcon icon={faSitemap} /> Departments &amp; Designations
                </h3>
                <p style={{ color: 'light-dark(#777, var(--theme-muted, #959ba5))', fontSize: '14px', margin: '10px 0 0', lineHeight: '1.6' }}>
                    The choices offered on employee profiles. Renaming one updates every employee who has it. The number shows how many employees use each one; an entry in use cannot be deleted. The order here is the order lists and boards show.
                </p>
            </div>

            <div style={{ flex: '1', minWidth: '280px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '24px' }}>
                <OrgList kind="department" title="Departments" singular="department" rows={entries.departments} loading={loading} />
                <OrgList kind="designation" title="Designations" singular="designation" rows={entries.designations} loading={loading} />
            </div>
        </div>
    );
};

export default OrgOptionsManager;
